import { Card, CardHeader, CardTable, SpinnerButton, createStyles, makeStyles } from '@forgeglobal/design-framework'
import {
  IssuerSortColumn,
  PricedIssuerFragment,
  SortDirection,
  useGetPricedIssuersQuery,
} from 'graphql/bindings/AdminBindings'
import React, { Fragment } from 'react'

import { PricedIssuerRow } from './PricedIssuerRow'

interface PricedIssuersTableProps {
  header: string
}

const ITEM_LIMIT = 50

const useStyles = makeStyles(() =>
  createStyles({
    column_header: {
      display: 'flex',
      justifyContent: 'center',
    },
  })
)

export const PricedIssuersTable: React.FC<PricedIssuersTableProps> = ({ header }) => {
  const classes = useStyles()
  const defaultTableVars = {
    limit: ITEM_LIMIT,
    offset: 0,
    sortingDirection: SortDirection.Ascending,
    sortBy: IssuerSortColumn.Name,
  }

  const { data, loading, refetch } = useGetPricedIssuersQuery({
    variables: {
      ...defaultTableVars,
    },
    fetchPolicy: 'network-only',
  })

  const issuers = data?.pricedIssuers.data ?? []

  const handleSort = (column: string, direction?: string) => {
    const fieldColMap: { [key: string]: IssuerSortColumn } = {
      issuer: IssuerSortColumn.Name,
      default_share_price_shareholder: IssuerSortColumn.ImpliedBid,
      default_share_price_investor: IssuerSortColumn.ImpliedAsk,
      share_price_variance: IssuerSortColumn.Variance,
      weight: IssuerSortColumn.Weight,
      show_pricing_data: IssuerSortColumn.IsPricingDataDisplayed,
      featured: IssuerSortColumn.IsFeatured,
      min_transaction_size: IssuerSortColumn.MinTransactionSize,
      is_available: IssuerSortColumn.IsAvailable,
    }

    const directionMap: { [key: string]: SortDirection } = {
      asc: SortDirection.Ascending,
      desc: SortDirection.Descending,
    }

    refetch({
      ...defaultTableVars,
      sortBy: fieldColMap[column],
      sortingDirection: directionMap[direction ?? ''],
    })
  }

  const columnDefs = [
    {
      name: 'issuer',
      label: 'Issuer',
      minWidth: 200,
      isSortable: true,
    },
    {
      name: 'default_share_price_shareholder',
      label: 'Trading range (low)',
      minWidth: 125,
      isSortable: true,
      priority: 2,
    },
    {
      name: 'default_share_price_investor',
      label: 'Trading range (high)',
      minWidth: 125,
      isSortable: true,
      priority: 3,
    },
    {
      name: 'share_price_variance',
      label: 'Variance',
      minWidth: 100,
      isSortable: true,
      priority: 4,
    },
    {
      name: 'weight',
      label: 'Sort Rank',
      minWidth: 100,
      isSortable: true,
      priority: 5,
    },
    {
      name: 'show_pricing_data',
      label: <div className={classes.column_header}>Pricing Displayed?</div>,
      minWidth: 100,
      isSortable: true,
      priority: 6,
    },
    {
      name: 'featured',
      label: <div className={classes.column_header}>Featured?</div>,
      minWidth: 100,
      isSortable: true,
      priority: 7,
    },
    {
      name: 'is_available',
      label: <div className={classes.column_header}>Available?</div>,
      minWidth: 100,
      isSortable: true,
      priority: 8,
    },
    {
      name: 'min_transaction_size',
      label: 'Min. Transaction Size',
      minWidth: 150,
      isSortable: true,
      priority: 9,
    },
    {
      name: 'owners',
      label: 'Owners',
      minWidth: 400,
      priority: 10,
    },
  ]

  return (
    <Fragment>
      <CardTable
        onSort={handleSort}
        colDefs={columnDefs}
        header={header}
        count={data?.pricedIssuers.count}
        onPageChange={(newOffset: number) => {
          refetch({
            ...defaultTableVars,
            offset: newOffset,
          })
        }}
        pageSize={!loading && ITEM_LIMIT}
        offset={data?.pricedIssuers.offset}>
        {issuers.map((issuer: PricedIssuerFragment) => {
          return (
            <PricedIssuerRow
              issuer={issuer}
              limit={ITEM_LIMIT}
              offset={data?.pricedIssuers.offset ?? 0}
              key={issuer.id}
            />
          )
        })}
        {issuers.length === 0 && !loading && (
          <Card>
            <CardHeader title={'None'} />
          </Card>
        )}
        {loading ? (
          <Card>
            <CardHeader
              title={
                <SpinnerButton
                  variant="text"
                  color="primary"
                  type="submit"
                  fullWidth
                  loading={true}
                  caption="Loading"
                />
              }
            />
          </Card>
        ) : null}
      </CardTable>
    </Fragment>
  )
}
